/**
 * Tout ce qu'une annonce peut porter : types de véhicule, équipements, moyens
 * de paiement, caractéristiques techniques.
 *
 * C'est le pendant back-office de trois tables qui existent déjà ailleurs et
 * qui doivent rester identiques, sous peine d'écrire en base des valeurs que
 * personne ne saura relire :
 *
 *   * `VEHICLE_SPEC_FIELDS` dans `vehicle.controller.js` — l'API FILTRE
 *     silencieusement les clés qu'elle ne connaît pas et les valeurs hors
 *     domaine. Une clé inventée ici serait perdue à l'enregistrement, sans
 *     erreur, et l'admin croirait avoir saisi une caractéristique.
 *   * `VehicleSpecs.kt` et `VehicleFeatures.kt` dans l'app KMP — ce sont ses
 *     écrans qui affichent ces valeurs au locataire. Un libellé divergent ici
 *     ne se verrait pas : les clés partent à l'API, les libellés restent
 *     locaux, et seules les clés comptent.
 *
 * Les valeurs stockées sont TOUJOURS les clés, jamais les libellés français :
 * c'est `gasoline` qui part à l'API et « Essence » qui s'affiche.
 */

export type VehicleTypeKey =
  | 'car' | 'van' | 'motorcycle' | 'scooter' | 'trottinette'
  | 'bike' | 'quad' | 'jetski' | 'boat';

export type VehicleTypeMeta = {
  icon: string;
  singular: string;
  plural: string;
  color: string;
};

/**
 * Les neuf types acceptés par la contrainte `Vehicle_vehicle_type_check`
 * (migration 032 de l'API).
 *
 * La Flotte n'en connaissait que cinq, et appelait `scooter` « Trottinette » —
 * alors que `trottinette` est un type à part entière, celui des engins
 * électriques. Un utilitaire, un jetski ou un bateau publié depuis l'app
 * n'était donc modifiable qu'au prix d'une requalification silencieuse en
 * voiture, dès que l'admin enregistrait le formulaire.
 */
export const VEHICLE_TYPE_META: Record<VehicleTypeKey, VehicleTypeMeta> = {
  car:         { icon: '🚗', singular: 'Voiture',     plural: 'Voitures',     color: '#2563EB' },
  van:         { icon: '🚐', singular: 'Utilitaire',  plural: 'Utilitaires',  color: '#0EA5E9' },
  motorcycle:  { icon: '🏍️', singular: 'Moto',        plural: 'Motos',        color: '#4F46E5' },
  scooter:     { icon: '🛵', singular: 'Scooter',     plural: 'Scooters',     color: '#9333EA' },
  trottinette: { icon: '🛴', singular: 'Trottinette', plural: 'Trottinettes', color: '#A855F7' },
  bike:        { icon: '🚴', singular: 'Vélo',        plural: 'Vélos',        color: '#10B981' },
  quad:        { icon: '🏎️', singular: 'Quad',        plural: 'Quads',        color: '#F97316' },
  jetski:      { icon: '🌊', singular: 'Jetski',      plural: 'Jetskis',      color: '#06B6D4' },
  boat:        { icon: '⛵', singular: 'Bateau',      plural: 'Bateaux',      color: '#0891B2' },
};

export const VEHICLE_TYPE_ORDER: VehicleTypeKey[] = [
  'car', 'van', 'motorcycle', 'scooter', 'trottinette', 'bike', 'quad', 'jetski', 'boat'
];

export const vehicleTypeMeta = (type: string): VehicleTypeMeta =>
  VEHICLE_TYPE_META[type as VehicleTypeKey] || VEHICLE_TYPE_META.car;

// ---------------------------------------------------------------------------
// Équipements — présents ou absents, sans valeur associée (colonne `features`)
// ---------------------------------------------------------------------------

export type Option = { key: string; label: string };

/** Miroir de `vehicleFeatureOptionsFor` (VehicleFeatures.kt). */
export function featureOptionsFor(vehicleType: string): Option[] {
  switch (vehicleType) {
    case 'car':
    case 'van':
      return [
        { key: 'air_conditioning', label: 'Climatisation' },
        { key: 'abs', label: 'ABS' },
        { key: 'bluetooth', label: 'Bluetooth' },
        { key: 'gps', label: 'GPS' },
      ];
    case 'motorcycle':
    case 'scooter':
    case 'quad':
      return [
        { key: 'helmet', label: 'Casque inclus' },
        { key: 'abs', label: 'ABS' },
        { key: 'top_case', label: 'Top case' },
        { key: 'phone_mount', label: 'Support téléphone' },
        { key: 'usb', label: 'Chargeur USB' },
      ];
    case 'bike':
      return [
        { key: 'helmet', label: 'Casque inclus' },
        { key: 'lock', label: 'Antivol' },
        { key: 'lights', label: 'Lumières' },
        { key: 'basket', label: 'Panier' },
        { key: 'child_seat', label: 'Siège enfant' },
      ];
    case 'jetski':
    case 'boat':
      return [
        { key: 'life_jacket', label: 'Gilets de sauvetage' },
        { key: 'trailer', label: 'Remorque incluse' },
        { key: 'skipper', label: 'Skipper inclus' },
        { key: 'gps', label: 'GPS' },
      ];
    case 'trottinette':
      return [
        { key: 'helmet', label: 'Casque inclus' },
        { key: 'lock', label: 'Antivol' },
        { key: 'lights', label: 'Lumières' },
        { key: 'phone_mount', label: 'Support téléphone' },
      ];
    default:
      return [
        { key: 'helmet', label: 'Casque inclus' },
        { key: 'lock', label: 'Antivol' },
        { key: 'lights', label: 'Lumières' },
      ];
  }
}

/**
 * Moyens de paiement déclarés (colonne `payment_methods`).
 *
 * Rappel utile en relisant cet écran : Dingo n'encaisse rien. Ces moyens se
 * règlent en main propre entre le locataire et le propriétaire ; l'annonce les
 * publie pour que les deux arrivent d'accord.
 */
export const PAYMENT_METHOD_OPTIONS: Option[] = [
  { key: 'card', label: 'Carte bancaire' },
  { key: 'cash', label: 'Espèces' },
  { key: 'transfer', label: 'Virement' },
  { key: 'wallet', label: 'Portefeuille mobile' },
];

// ---------------------------------------------------------------------------
// Caractéristiques techniques — une clé, une valeur (colonne `specs`, JSONB)
// ---------------------------------------------------------------------------

export type SpecInput =
  | { kind: 'choice'; options: Option[] }
  | { kind: 'int'; min: number; max: number }
  | { kind: 'decimal'; min: number; max: number }
  | { kind: 'text'; max: number };

export type SpecField = {
  key: string;
  label: string;
  input: SpecInput;
  unit?: string;
  placeholder?: string;
};

const ENERGY_OPTIONS: Option[] = [
  { key: 'gasoline', label: 'Essence' },
  { key: 'diesel', label: 'Diesel' },
  { key: 'electric', label: 'Électrique' },
  { key: 'hybrid', label: 'Hybride' },
  { key: 'lpg', label: 'GPL' },
];

/** Ni diesel ni GPL sur un deux-roues : ces énergies n'existent pas sur le segment. */
const TWO_WHEELER_ENERGY = ENERGY_OPTIONS.filter((o) => ['gasoline', 'electric', 'hybrid'].includes(o.key));

const YES_NO: Option[] = [
  { key: 'yes', label: 'Oui' },
  { key: 'no', label: 'Non' },
];

const energyField = (options: Option[] = ENERGY_OPTIONS): SpecField =>
  ({ key: 'energy', label: 'Énergie', input: { kind: 'choice', options } });

const seatsField = (max: number): SpecField =>
  ({ key: 'seats', label: 'Nombre de places', input: { kind: 'int', min: 1, max }, placeholder: 'Ex : 4' });

const ENGINE_CC: SpecField =
  { key: 'engine_cc', label: 'Cylindrée', input: { kind: 'int', min: 25, max: 3000 }, unit: 'cm³', placeholder: 'Ex : 125' };

const POWER_HP: SpecField =
  { key: 'power_hp', label: 'Puissance', input: { kind: 'int', min: 1, max: 5000 }, unit: 'ch', placeholder: 'Ex : 150' };

const RANGE_KM: SpecField =
  { key: 'range_km', label: 'Autonomie', input: { kind: 'int', min: 1, max: 1000 }, unit: 'km', placeholder: 'Ex : 45' };

const LICENSE: SpecField = {
  key: 'license_category',
  label: 'Permis exigé',
  input: {
    kind: 'choice',
    options: [
      { key: 'none', label: 'Aucun' },
      { key: 'A1', label: 'A1' },
      { key: 'A2', label: 'A2' },
      { key: 'A', label: 'A' },
      { key: 'B', label: 'B' },
      { key: 'boat', label: 'Côtier' },
    ],
  },
};

/**
 * Les trois caractéristiques mises en avant sur les cartes de l'app, par type.
 * Miroir de `vehicleCardSpecFieldsFor`.
 */
export function cardSpecFieldsFor(vehicleType: string): SpecField[] {
  switch (vehicleType) {
    case 'car':
    case 'van':
      return [
        energyField(),
        seatsField(9),
        {
          key: 'transmission',
          label: 'Boîte de vitesses',
          input: { kind: 'choice', options: [{ key: 'manual', label: 'Manuelle' }, { key: 'automatic', label: 'Automatique' }] },
        },
      ];
    case 'motorcycle':
      return [ENGINE_CC, LICENSE, energyField(TWO_WHEELER_ENERGY)];
    case 'scooter':
      return [ENGINE_CC, seatsField(2), energyField(TWO_WHEELER_ENERGY)];
    case 'trottinette':
      return [
        RANGE_KM,
        { key: 'max_speed_kmh', label: 'Vitesse maximale', input: { kind: 'int', min: 1, max: 400 }, unit: 'km/h', placeholder: 'Ex : 25' },
        { key: 'foldable', label: 'Pliable', input: { kind: 'choice', options: YES_NO } },
      ];
    case 'bike':
      return [
        {
          key: 'bike_type',
          label: 'Type de vélo',
          input: {
            kind: 'choice',
            options: [
              { key: 'city', label: 'Ville' },
              { key: 'mtb', label: 'VTT' },
              { key: 'road', label: 'Route' },
              { key: 'ebike', label: 'Électrique' },
            ],
          },
        },
        {
          key: 'frame_size',
          label: 'Taille du cadre',
          input: { kind: 'choice', options: ['S', 'M', 'L', 'XL'].map((s) => ({ key: s, label: s })) },
        },
        { key: 'gears', label: 'Nombre de vitesses', input: { kind: 'int', min: 1, max: 40 }, placeholder: 'Ex : 21' },
      ];
    case 'quad':
      return [
        ENGINE_CC,
        seatsField(4),
        {
          key: 'drivetrain',
          label: 'Transmission',
          input: { kind: 'choice', options: [{ key: '2wd', label: '2 roues motrices' }, { key: '4wd', label: '4 roues motrices' }] },
        },
      ];
    case 'jetski':
      return [POWER_HP, seatsField(4), LICENSE];
    case 'boat':
      return [
        { key: 'length_m', label: 'Longueur', input: { kind: 'decimal', min: 1, max: 100 }, unit: 'm', placeholder: 'Ex : 7.5' },
        seatsField(60),
        POWER_HP,
      ];
    default:
      return [energyField(), seatsField(9), RANGE_KM];
  }
}

/**
 * Conditions chiffrées de fin de location, pour les véhicules motorisés.
 * Miroir de `vehicleConditionSpecFieldsFor`.
 */
export function conditionSpecFieldsFor(vehicleType: string): SpecField[] {
  const motorized = ['car', 'van', 'motorcycle', 'scooter', 'quad', 'jetski', 'boat'].includes(vehicleType);
  if (!motorized) return [];

  const fields: SpecField[] = [
    {
      key: 'mileage_policy',
      label: 'Kilométrage',
      input: { kind: 'choice', options: [{ key: 'unlimited', label: 'Illimité' }, { key: 'capped', label: 'Plafonné' }] },
    },
    { key: 'mileage_per_day', label: 'Kilomètres inclus par jour', input: { kind: 'int', min: 1, max: 5000 }, unit: 'km/j', placeholder: 'Ex : 200' },
    {
      key: 'fuel_policy',
      label: 'Carburant',
      input: {
        kind: 'choice',
        options: [
          { key: 'full_to_full', label: 'Plein à rendre' },
          { key: 'same_level', label: 'Même niveau' },
          { key: 'included', label: 'Carburant inclus' },
        ],
      },
    },
    { key: 'min_age', label: 'Âge minimum du conducteur', input: { kind: 'int', min: 16, max: 99 }, unit: 'ans', placeholder: 'Ex : 21' },
    { key: 'license_years', label: 'Ancienneté du permis', input: { kind: 'int', min: 0, max: 50 }, unit: 'ans', placeholder: 'Ex : 2' },
    { key: 'color', label: 'Couleur', input: { kind: 'text', max: 40 }, placeholder: 'Ex : Gris' },
  ];

  if (['car', 'van'].includes(vehicleType)) {
    fields.push({ key: 'luggage', label: 'Bagages', input: { kind: 'int', min: 0, max: 20 }, placeholder: 'Ex : 3' });
  }

  return fields;
}

/**
 * Tous les champs saisissables pour un type.
 *
 * L'autonomie est ajoutée aux vélos : l'app la substitue au nombre de vitesses
 * dès que `bike_type` vaut `ebike`, et les deux clés doivent donc être
 * éditables ici — sinon l'admin ne pourrait pas corriger l'autonomie d'un VAE.
 */
export function allSpecFieldsFor(vehicleType: string): SpecField[] {
  const fields = [...cardSpecFieldsFor(vehicleType)];
  if (vehicleType === 'bike') fields.push(RANGE_KM);
  return [...fields, ...conditionSpecFieldsFor(vehicleType)];
}

/**
 * Nettoie la carte de caractéristiques avant envoi : les valeurs vides
 * disparaissent (une clé à chaîne vide serait de toute façon ignorée par
 * l'API), et les clés étrangères au type courant sont conservées.
 *
 * Conservées, et non purgées : requalifier une moto en scooter ne doit pas
 * effacer sa cylindrée, que le propriétaire a bien saisie et que l'API accepte
 * quel que soit le type. C'est aussi le comportement de l'app.
 */
export function cleanSpecs(specs: Record<string, string>): Record<string, string> {
  const cleaned: Record<string, string> = {};
  Object.entries(specs || {}).forEach(([key, value]) => {
    const text = String(value ?? '').trim();
    if (text !== '') cleaned[key] = text;
  });
  return cleaned;
}
